/**
 * This file is a part of Amblique's projects.
 *
 * Copyright © Amblique - All Rights Reserved.
 *
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 */

import {providers} from '@amblique/base/app/plugins/video/providers/providers';

/**
 * @typedef {import('@amblique/base/app/plugins/video/providers/abstract-video').AbstractVideo} AbstractVideo
 */

/**
 * Video Factory
 *
 * Helper for creating video player instances from Vimeo/YouTube IDs.
 * Wraps the video model classes with a simple API for React components.
 */
export const videoFactory = {
  /**
   * Detect video provider from input string
   *
   * @param {string} input - Video ID or URL
   * @returns {Object|null} Provider class or null if not supported
   */
  detectProvider(input) {
    if (!input) return null;

    return providers.find((Provider) => Provider.TYPE_PATTERN.test(input));
  },

  /**
   * Extract video ID from input string
   *
   * @param {string} input - Video ID or URL
   * @param {Object} Provider - Provider class
   * @returns {string|null} Video ID or null if not found
   */
  extractId(input, Provider) {
    if (!input || !Provider) return null;

    const match = input.match(Provider.ID_PATTERN);

    return match ? match[1] : null;
  },

  /**
   * Parse video input to get provider and ID
   *
   * @param {string} input - Video ID or URL
   * @returns {{provider: Object, id: string, type: string}|null}
   */
  parseVideo(input) {
    const Provider = this.detectProvider(input);

    if (!Provider) {
      return null;
    }

    const id = this.extractId(input, Provider);

    if (!id) {
      return null;
    }

    return {
      provider: Provider,
      id,
      type: Provider.TYPE,
    };
  },

  /**
   * Create video player instance
   *
   * @param {string} videoId - Video ID or URL
   * @param {HTMLElement} container - Container element
   * @param {Object} options - Video options
   * @returns {Promise<AbstractVideo>} Player instance with metadata
   *
   * @example
   * const { video, player, metadata } = await videoFactory.createPlayer(
   *   '16401445',
   *   containerRef.current,
   *   { loop: true, mute: true }
   * );
   */
  async createPlayer(videoId, container, options = {}) {
    const parsed = this.parseVideo(videoId);

    if (!parsed) {
      throw new Error(`Unsupported video provider for: ${videoId}`);
    }

    const {provider: VideoProvider, id} = parsed;
    const video = new VideoProvider(container, {...options, id});

    await video.init();

    return video;
  },

  /**
   * Check if input is a valid video ID/URL
   *
   * @param {string} input - Video ID or URL
   * @returns {boolean}
   */
  isValidVideo(input) {
    return this.detectProvider(input) !== null;
  },
};
